import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  coversApi,
  templatesApi,
  uploadToSignedUrl,
  LEGACY_ALBUM_CATEGORY_TO_SLUG,
  type LayoutTemplate,
  type LayoutTemplateSlot,
} from '@/lib/api';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { SlotEditor } from './SlotEditor';
import { detectTransparentSlots } from '@/lib/templateAutoDetect';
// Task 14: "Remplacer le SVG" now shares the upload pipeline with
// UploadTemplateDialog — detect + classify + review pane — via
// prepareTemplateUploads. Single-file replace just wraps the file in a
// queue of 1. The review pane is the same /admin/layouts/review route;
// QueuedTemplate.editingTemplateId tells it to PATCH instead of POST on save.
import { prepareTemplateUploads } from './uploadFlow';

interface TemplateEditDialogProps {
  template: LayoutTemplate;
  onClose: () => void;
}

export function TemplateEditDialog({ template, onClose }: TemplateEditDialogProps) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const replaceInputRef = useRef<HTMLInputElement>(null);
  const svgReplaceInputRef = useRef<HTMLInputElement>(null);

  const [name, setName] = useState(template.name);
  const [description, setDescription] = useState(template.description ?? '');
  // Unified cover-taxonomy multi-select. Falls back to the legacy single
  // English enum (mapped to its slug) when album_categories is not yet set.
  const [albumCategories, setAlbumCategories] = useState<string[]>(() => {
    if (template.album_categories && template.album_categories.length > 0) {
      return template.album_categories;
    }
    if (template.album_category) {
      const slug = LEGACY_ALBUM_CATEGORY_TO_SLUG[template.album_category];
      return slug ? [slug] : [];
    }
    return [];
  });
  const [tagsInput, setTagsInput] = useState((template.tags ?? []).join(', '));
  const [isVisible, setIsVisible] = useState(template.is_visible);
  const [isAiEligible, setIsAiEligible] = useState(template.is_ai_eligible);
  const [slots, setSlots] = useState<LayoutTemplateSlot[]>(template.slots ?? []);
  const [backgroundUrl, setBackgroundUrl] = useState(template.background_url);
  const [backgroundStoragePath, setBackgroundStoragePath] = useState(template.background_storage_path);
  const [replaceProgress, setReplaceProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [svgReplacing, setSvgReplacing] = useState(false);
  const [reEditing, setReEditing] = useState(false);
  const [svgReplaceError, setSvgReplaceError] = useState<string | null>(null);
  // TODO(Task 9): setReplacedAssets is wired by the now-stubbed SVG-replace
  // flow. Prefixed with `_` to satisfy noUnusedLocals until Tasks 9-14
  // rewire it against the hybrid pipeline.
  const [replacedAssets, _setReplacedAssets] = useState<{
    original_svg_path: string | null;
    overlay_svg_path: string | null;
    overlay_print_png_path: string;
    background_storage_path: string;
  } | null>(null);
  void _setReplacedAssets;

  // Cover categories — same taxonomy (and cache key) as the covers admin +
  // cover picker chips.
  const { data: categoriesData } = useQuery({
    queryKey: ['cover-categories'],
    queryFn: () => coversApi.categories(),
    staleTime: 60_000,
  });
  const coverCategories = categoriesData?.categories ?? [];

  function toggleCategory(slug: string) {
    setAlbumCategories((prev) =>
      prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug],
    );
  }

  // Free a blob URL when the user has uploaded a replacement that's still
  // a local object URL (replace.onSuccess swaps to the public R2 URL).
  const replacedLocalUrl = useRef<string | null>(null);
  useEffect(() => () => {
    if (replacedLocalUrl.current) URL.revokeObjectURL(replacedLocalUrl.current);
  }, []);

  const replace = useMutation({
    mutationFn: async (file: File) => {
      setReplaceProgress(0);
      if (!file.type.startsWith('image/png')) {
        throw new Error('Le fichier doit être un PNG.');
      }
      const signed = await templatesApi.getUploadUrl(file.name, file.type);
      await uploadToSignedUrl(signed.upload_url, file, file.type, setReplaceProgress);
      const detected = await detectTransparentSlots(file).catch(() => [] as LayoutTemplateSlot[]);
      return { signed, detected };
    },
    onSuccess: ({ signed, detected }) => {
      if (replacedLocalUrl.current) URL.revokeObjectURL(replacedLocalUrl.current);
      replacedLocalUrl.current = null;
      setBackgroundUrl(signed.public_url);
      setBackgroundStoragePath(signed.storage_path);
      setSlots(detected);
      setReplaceProgress(0);
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : 'Échec du remplacement');
      setReplaceProgress(0);
    },
  });

  function handleReplaceClick() {
    replaceInputRef.current?.click();
  }
  function handleReplaceFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    replace.mutate(f);
  }

  // Re-open the "Review photo slots" (SLOT/Decoration) interface for THIS
  // already-saved template — using its OWN current SVG (no re-upload needed).
  // Reuses the exact replace-SVG pipeline (detect + classify → /review), with
  // editingTemplateId set so saving PATCHes this template instead of creating
  // a new one. Lets the admin re-classify slots any time after import.
  async function handleReEditSlots() {
    const svgUrl = template.original_svg_url;
    if (!svgUrl) { setSvgReplaceError('Ce modèle n\'a pas de SVG source à ré-éditer.'); return; }
    setReEditing(true);
    setSvgReplaceError(null);
    try {
      const res = await fetch(svgUrl);
      if (!res.ok) throw new Error('Téléchargement du SVG échoué');
      const blob = await res.blob();
      const file = new File([blob], `${template.slug || 'template'}.svg`, { type: 'image/svg+xml' });
      const { ok, failed } = await prepareTemplateUploads([file], {
        editingTemplateId: template.id,
        initialMetadata: {
          name: template.name,
          slug: template.slug,
          category: template.album_category ?? undefined,
          isVisible: template.is_visible,
          isAiEligible: template.is_ai_eligible,
        },
      });
      if (ok.length === 0) { setSvgReplaceError(failed[0]?.error ?? 'Erreur de traitement'); return; }
      onClose();
      navigate('/admin/layouts/review');
    } catch (err) {
      setSvgReplaceError(err instanceof Error ? err.message : 'Erreur de traitement');
    } finally {
      setReEditing(false);
    }
  }

  async function handleSvgReplaceFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;

    setSvgReplacing(true);
    setSvgReplaceError(null);
    try {
      const { ok, failed } = await prepareTemplateUploads([f], {
        editingTemplateId: template.id,
        initialMetadata: {
          name: template.name,
          slug: template.slug,
          category: template.album_category ?? undefined,
          isVisible: template.is_visible,
          isAiEligible: template.is_ai_eligible,
        },
      });
      if (ok.length === 0) {
        // Single-file flow — if it failed, surface the first (only) error.
        setSvgReplaceError(failed[0]?.error ?? 'Erreur de traitement');
        return;
      }
      onClose();
      navigate('/admin/layouts/review');
    } catch (err) {
      setSvgReplaceError(err instanceof Error ? err.message : 'Erreur de traitement');
    } finally {
      setSvgReplacing(false);
    }
  }

  const save = useMutation({
    mutationFn: async () => {
      const tags = tagsInput
        .split(/[,;]+/)
        .map((t) => t.trim().toLowerCase())
        .filter((t) => t.length > 0);
      return templatesApi.update(template.id, {
        name: name.trim(),
        description: description.trim() || null,
        background_storage_path: backgroundStoragePath,
        slots,
        slot_count: slots.length,
        tags,
        is_visible: isVisible,
        album_categories: albumCategories,
        is_ai_eligible: isAiEligible,
        ...(replacedAssets ?? {}),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-templates'] });
      onClose();
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : 'Erreur de sauvegarde');
    },
  });

  const del = useMutation({
    mutationFn: () => templatesApi.delete(template.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-templates'] });
      onClose();
    },
  });

  function handleDelete() {
    if (confirm(`Supprimer "${template.name}" définitivement ?`)) {
      del.mutate();
    }
  }

  return (
    <Modal isOpen={true} onClose={onClose} size="full">
      <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">{template.name}</h2>
          <p className="text-sm text-gray-500">Modifier le modèle</p>
        </div>
        <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-500">
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" />
          </svg>
        </button>
      </div>

      <div className="grid lg:grid-cols-[1fr_380px] gap-0 max-h-[78vh]">
        <div className="p-6 bg-gray-50 overflow-auto flex items-start justify-center">
          <div className="w-full max-w-md">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-lg mb-4">
                {error}
              </div>
            )}
            {backgroundUrl ? (
              <SlotEditor imageUrl={backgroundUrl} slots={slots} onChange={setSlots} />
            ) : (
              <p className="text-sm text-gray-500">Pas d'image de fond.</p>
            )}

            <div className="mt-3 space-y-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={handleReplaceClick}
                loading={replace.isPending}
                className="w-full"
              >
                {replace.isPending ? 'Téléversement…' : 'Remplacer le PNG'}
              </Button>
              {replace.isPending && (
                <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                  <div className="h-full bg-brand-500 transition-all" style={{ width: `${replaceProgress}%` }} />
                </div>
              )}
              <input
                ref={replaceInputRef}
                type="file"
                accept="image/png"
                onChange={handleReplaceFile}
                className="hidden"
              />
              <p className="text-xs text-gray-400 leading-relaxed">
                Le remplacement relance la détection automatique des zones.
                Les zones existantes seront écrasées.
              </p>

              <div className="flex flex-col gap-2 pt-2 border-t border-gray-200 mt-2">
                {/* Re-open the SLOT/Decoration review for THIS template using its
                    own SVG — lets the admin re-edit zones after import. */}
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleReEditSlots}
                  loading={reEditing}
                  disabled={!template.original_svg_url}
                  className="w-full"
                >
                  {reEditing ? 'Ouverture…' : '✏️ Ré-éditer les emplacements (IA)'}
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => svgReplaceInputRef.current?.click()}
                  loading={svgReplacing}
                  className="w-full"
                >
                  {svgReplacing ? 'Traitement…' : 'Remplacer le SVG'}
                </Button>
              </div>
              {replacedAssets && (
                <p className="text-xs text-green-600">Nouvel SVG en attente d'enregistrement</p>
              )}
              {svgReplaceError && (
                <p className="text-xs text-red-600">{svgReplaceError}</p>
              )}
              <input
                ref={svgReplaceInputRef}
                type="file"
                accept="image/svg+xml,.svg"
                onChange={handleSvgReplaceFile}
                className="hidden"
              />
            </div>
          </div>
        </div>

        <div className="border-l border-gray-100 overflow-auto p-5 space-y-4">
          <Field label="Nom">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
            />
          </Field>
          <Field label="Catégories d'album">
            <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-[440px] overflow-auto">
              {coverCategories.length === 0 ? (
                <p className="text-sm text-gray-400 px-3 py-2">Chargement des catégories…</p>
              ) : (
                coverCategories.map((c) => (
                  <label
                    key={c.slug}
                    className="flex items-center gap-2.5 px-3 py-2 cursor-pointer hover:bg-gray-50"
                  >
                    <input
                      type="checkbox"
                      checked={albumCategories.includes(c.slug)}
                      onChange={() => toggleCategory(c.slug)}
                      className="w-4 h-4 accent-brand-500"
                    />
                    <span className="text-sm text-gray-900">
                      {c.icon ? `${c.icon} ` : ''}{c.label_fr}
                    </span>
                  </label>
                ))
              )}
            </div>
            <p className="text-xs text-gray-400 mt-1.5">
              Aucune cochée = utilisable pour toutes les catégories
            </p>
          </Field>
          <Field label="Tags de style">
            <input
              type="text"
              value={tagsInput}
              onChange={(e) => setTagsInput(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
            />
          </Field>
          <Field label="Description">
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
            />
          </Field>
          <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
            <input
              type="checkbox"
              checked={isVisible}
              onChange={(e) => setIsVisible(e.target.checked)}
              className="w-4 h-4 accent-brand-500"
            />
            <div className="flex-1 text-sm">
              <p className="font-medium text-gray-900">Visible pour les clients</p>
            </div>
          </label>
          <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
            <input
              type="checkbox"
              checked={isAiEligible}
              onChange={(e) => setIsAiEligible(e.target.checked)}
              className="w-4 h-4 accent-brand-500"
            />
            <div className="flex-1 text-sm">
              <p className="font-medium text-gray-900">L'IA peut utiliser ce modèle</p>
            </div>
          </label>

        </div>
      </div>

      <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between bg-gray-50">
        <Button variant="danger" size="sm" onClick={handleDelete} loading={del.isPending}>
          Supprimer
        </Button>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={onClose}>Annuler</Button>
          <Button onClick={() => save.mutate()} loading={save.isPending}>Enregistrer</Button>
        </div>
      </div>
    </Modal>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1.5">{label}</label>
      {children}
    </div>
  );
}
